import { useEffect, useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import type { ApiError } from '../lib/api';
import { AuthCard, Label, PasswordInput, authFieldClass } from '../components/AuthCard';
import { api, isStaff } from '../lib/api';
import { useAuth } from '../lib/auth';
import { SSO, SsoNote, providerFor, ssoHref } from '../lib/sso';

/*
  Padlock as the show/hide control: shackle closed while the password is
  masked, swung open once it is visible. One glyph, two states — the label and
  aria-pressed carry the meaning that the picture alone cannot.
*/
/* Circled "i" for the note under the sign-in buttons. */
const infoIcon = (
  <svg
    aria-hidden
    viewBox="0 0 24 24"
    className="mt-0.5 h-5 w-5 shrink-0 text-ink-400"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5.5" />
    <circle cx="12" cy="7.75" r="0.6" fill="currentColor" stroke="none" />
  </svg>
);


/*
  Set by an SSO callback when it sends someone back without a session. It names
  the provider it came from, so the message can too.
*/
const ssoMessage = (reason: string, provider: string): string | undefined => {
  const label = SSO.find((p) => p.id === provider)?.label ?? 'That provider';
  switch (reason) {
    case 'cancelled':
      return `${label} sign-in was cancelled.`;
    case 'failed':
      return `${label} sign-in did not complete. Please try again.`;
    case 'unverified':
      return `${label} could not confirm that the email address on that account belongs to you, `
        + 'so it cannot be used to sign in here.';
    case 'disabled':
      return 'That account has been deactivated. Contact the training office if this is unexpected.';
    case 'closed':
      return `No account found for this ${label} address. Registration is coming soon.`;
    default:
      return undefined;
  }
};

/**
 * Sign-in for the four roles. There is deliberately no self-registration —
 * accounts are created by a tech super admin, and an account that arrives via
 * an SSO provider stays inert until a tech super admin approves it.
 */
export default function Login() {
  const { user, loading, signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [ready, setReady] = useState<string[]>([]);
  // Closed until the server says otherwise, so a failed lookup never invites
  // someone to a form that will refuse them.
  const [registrationOpen, setRegistrationOpen] = useState(false);
  const [showSoon, setShowSoon] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();

  const from = (location.state as { from?: string } | null)?.from;
  const ssoError = ssoMessage(params.get('sso') ?? '', params.get('provider') ?? '');

  // A button only appears once the server confirms that provider's credentials
  // are configured, so a half-set-up deployment does not offer a route that 404s.
  useEffect(() => {
    let cancelled = false;
    api<{ providers: string[]; registrationOpen: boolean }>('/auth/providers')
      .then((r) => {
        if (cancelled) return;
        setReady(r.providers);
        setRegistrationOpen(r.registrationOpen);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  if (!loading && user) {
    return <Navigate to={from ?? (isStaff(user.role) ? '/admin' : '/')} replace />;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await signIn(email, password);
      navigate(from ?? '/', { replace: true });
    } catch (err) {
      setError((err as ApiError).message);
      setSubmitting(false);
    }
  }

  const buttons = SSO.filter((p) => ready.includes(p.id));

  // An address the provider owns has no password here, so point at its button.
  const owner = providerFor(email, ready);

  return (
    <AuthCard
      title="Sign in"
      footer={
        <>
          <p className="text-center text-base text-ink-600">
            No account yet?{' '}
            {registrationOpen ? (
              <Link
                to="/register"
                className="font-medium text-gold-600 underline-offset-2 hover:underline"
              >
                Register an account
              </Link>
            ) : (
              <button
                type="button"
                onClick={() => setShowSoon(true)}
                className="font-medium text-gold-600 underline-offset-2 hover:underline"
              >
                Register an account
              </button>
            )}
          </p>
          {/* Always mounted, so a screen reader announces the note when it fills. */}
          <div role="status">
            {showSoon && (
              <p className="mt-2 rounded-lg border border-gold-200 bg-gold-100 px-3 py-2 text-base font-medium text-gold-700">
                Registration is coming soon.
              </p>
            )}
          </div>
          <p className="mt-2 text-center text-base text-ink-400">
            Need help? Email{' '}
            <a href="mailto:office@fttmy.org" className="underline-offset-2 hover:underline">
              office@fttmy.org
            </a>
          </p>
          <Link
            to="/"
            className="mt-5 block text-center text-base text-ink-400 transition-colors hover:text-ink-900"
          >
            ← Back to the site
          </Link>
        </>
      }
    >
      {(error || ssoError) && (
        <div
          role="alert"
          className="mb-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-base text-red-800"
        >
          {error ?? ssoError}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5" noValidate>
        <div>
          <Label htmlFor="email" required>
            Email
          </Label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={`mt-2 ${authFieldClass}`}
          />
          {owner && (
            <SsoNote>
              {owner.label} addresses sign in with {owner.label} — use the button below.
            </SsoNote>
          )}
        </div>

        <div>
          <div className="flex items-baseline justify-between gap-3">
            <Label htmlFor="password" required>
              Password
            </Label>
            <Link
              to="/forgot-password"
              className="text-sm text-ink-400 transition-colors hover:text-gold-600"
            >
              Forgot password?
            </Link>
          </div>
          <PasswordInput
            id="password"
            name="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-2"
          />
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="w-full rounded-lg bg-ink-800 px-6 py-3.5 text-base font-medium text-white transition-colors hover:bg-ink-900 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>
      </form>

      {/*
        Below the form, not above it. Every account today signs in with a
        password, so that is the primary path; the providers are the alternative
        — and the only route for someone requesting access for the first time.
      */}
      {buttons.length > 0 && (
        <>
          <div className="my-7 flex items-center gap-4">
            <span className="h-px flex-1 bg-ink-100" />
            <span className="text-sm text-ink-400">or</span>
            <span className="h-px flex-1 bg-ink-100" />
          </div>

          {/* Plain links, not fetch: the OAuth handshake is a full-page redirect. */}
          <div className="space-y-3">
            {buttons.map((p) => (
              <a
                key={p.id}
                href={ssoHref(p.id, from)}
                className="flex w-full items-center justify-center gap-3 rounded-lg border border-ink-200 bg-white px-6 py-3.5 text-base font-medium text-ink-800 transition-colors hover:border-ink-300 hover:bg-ink-50"
              >
                {p.icon}
                Continue with {p.label}
              </a>
            ))}
          </div>

          {/*
            There is no separate registration, but these buttons are how a new
            person gets an account — they file a request for approval. Saying so
            is the difference between "I have no account here" and "I do not
            know how to get one".
            Only while registration is open — until then the buttons are for
            people who already have an account.
          */}
          {registrationOpen && (
            <p className="mt-3 text-center text-sm text-ink-500">
              You will be asked for a few more details before your request is sent.
            </p>
          )}
          {/* Both providers tell people to review our policy the first time they
              sign in; saying what we actually receive is the honest version. */}
          <div className="mt-4 flex gap-3 rounded-lg border border-ink-200 bg-ink-50 px-4 py-3 text-left text-sm text-ink-600">
            {infoIcon}
            <p>
              We receive only your name and email address, to identify your account.{' '}
              <Link
                to="/privacy-policy"
                className="font-medium text-gold-600 underline-offset-2 hover:underline"
              >
                Privacy Policy
              </Link>
            </p>
          </div>
        </>
      )}

      {/*
        Covers the people the buttons cannot: anyone on a Yahoo or church-domain
        address has no self-service route, so email is their way in. Worded to
        serve general enquiries too, and to still make sense when no SSO
        provider is configured and the buttons above are hidden.
      */}
    </AuthCard>
  );
}
